import { useState, useCallback } from 'react';

const STORAGE_KEY = 'library-reading-list';
const TTL_MS = 60 * 24 * 60 * 60 * 1000; // 60 days

function readFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    // Filter out expired entries
    const now = Date.now();
    const filtered = {};
    for (const [id, entry] of Object.entries(parsed)) {
      if (now - entry.addedAt <= TTL_MS) {
        filtered[id] = entry;
      }
    }
    return filtered;
  } catch {
    return {};
  }
}

function writeToStorage(data) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // Storage unavailable — fail silently
  }
}

export function useReadingList() {
  const [listMap, setListMap] = useState(() => readFromStorage());

  const persist = useCallback((nextMap) => {
    setListMap(nextMap);
    writeToStorage(nextMap);
  }, []);

  const toggle = useCallback((book) => {
    setListMap(prev => {
      const next = { ...prev };
      const key = String(book.id);
      if (next[key]) {
        delete next[key];
      } else {
        next[key] = {
          addedAt: Date.now(),
          title: book.title,
          author: book.author,
        };
      }
      writeToStorage(next);
      return next;
    });
  }, []);

  const remove = useCallback((bookId) => {
    setListMap(prev => {
      const next = { ...prev };
      delete next[String(bookId)];
      writeToStorage(next);
      return next;
    });
  }, []);

  const clear = useCallback(() => {
    persist({});
  }, [persist]);

  const isSaved = useCallback((bookId) => {
    return Boolean(listMap[String(bookId)]);
  }, [listMap]);

  // Array sorted newest first
  const items = Object.entries(listMap)
    .map(([bookId, entry]) => ({ bookId, ...entry }))
    .sort((a, b) => b.addedAt - a.addedAt);

  return { items, toggle, remove, clear, isSaved };
}
